import { useForm, Link, usePage } from '@inertiajs/react'
import { FormEvent, useEffect, useRef, useState } from 'react'
import { Eye, EyeOff, Loader2 } from 'lucide-react'
import AuthLayout, {
  AuthCardHeading,
  AuthMessage,
} from '@/layouts/AuthLayout'
import { PageProps } from '@/types'

interface LoginProps extends PageProps {
  /** Address from the last failed attempt, so the user need not retype it. */
  attempted_email?: string | null
  /** Friendly reason the last attempt failed (wrong credentials, deactivated
   *  account, expired session). */
  sign_in_error?: string | null
}

export default function Login() {
  const { flash, attempted_email, sign_in_error } = usePage<LoginProps>().props

  const { data, setData, post, processing, errors } = useForm({
    user: {
      email: attempted_email || '',
      password: '',
      remember_me: false,
    },
  })

  const [showPassword, setShowPassword] = useState(false)
  const passwordRef = useRef<HTMLInputElement>(null)
  const emailRef = useRef<HTMLInputElement>(null)

  // Changes only when a *new* rejected attempt comes back from the server, so
  // the reset below never fires while someone is typing.
  const rejectedAttempt = `${sign_in_error ?? ''}|${attempted_email ?? ''}`

  // The email survives a failed attempt, the password never does: clear it,
  // re-hide it, and put the cursor in the field the user has to retype.
  useEffect(() => {
    if (sign_in_error || attempted_email) {
      setData((previous) => ({
        ...previous,
        user: { ...previous.user, password: '' },
      }))
      setShowPassword(false)
      passwordRef.current?.focus()
    } else {
      emailRef.current?.focus()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rejectedAttempt])

  const formError =
    sign_in_error || flash?.alert || flash?.error || null
  const emailError = (errors as Record<string, string>)['user.email']
  const passwordError = (errors as Record<string, string>)['user.password']
  const credentialsRejected = Boolean(formError && attempted_email)

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    // preserveState is on by default for POSTs, which would keep the typed
    // password (and the "show password" toggle) alive when the server bounces
    // us back to this same page. Start the card fresh instead.
    post('/login', { preserveState: false })
  }

  return (
    <AuthLayout title="Sign In">
      <AuthCardHeading title="Sign in to your account" />

      {flash?.notice && !formError && (
        <AuthMessage tone="success">{flash.notice}</AuthMessage>
      )}
      {formError && (
        <div id="sign-in-error">
          <AuthMessage tone="error">{formError}</AuthMessage>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label htmlFor="email" className="auth-label">
            Email address
          </label>
          <input
            id="email"
            ref={emailRef}
            name="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            required
            value={data.user.email}
            onChange={(e) => setData('user', { ...data.user, email: e.target.value })}
            className="auth-input"
            aria-invalid={Boolean(emailError) || credentialsRejected}
            aria-describedby={
              emailError
                ? 'email-error'
                : credentialsRejected
                  ? 'sign-in-error'
                  : undefined
            }
          />
          {emailError && (
            <p id="email-error" className="mt-1.5 text-sm text-status-error">
              {emailError}
            </p>
          )}
        </div>

        <div>
          <div className="flex items-baseline justify-between">
            <label htmlFor="password" className="auth-label">
              Password
            </label>
            <button
              type="button"
              onClick={() => setShowPassword((visible) => !visible)}
              className="auth-link mb-1.5 inline-flex items-center gap-1 text-xs"
              aria-pressed={showPassword}
              aria-controls="password"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? (
                <>
                  <EyeOff className="h-3.5 w-3.5" aria-hidden="true" /> Hide
                </>
              ) : (
                <>
                  <Eye className="h-3.5 w-3.5" aria-hidden="true" /> Show
                </>
              )}
              <span className="sr-only"> password</span>
            </button>
          </div>
          <input
            id="password"
            ref={passwordRef}
            name="password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            required
            value={data.user.password}
            onChange={(e) => setData('user', { ...data.user, password: e.target.value })}
            className="auth-input"
            aria-invalid={Boolean(passwordError) || credentialsRejected}
            aria-describedby={
              passwordError
                ? 'password-error'
                : credentialsRejected
                  ? 'sign-in-error'
                  : undefined
            }
          />
          {passwordError && (
            <p id="password-error" className="mt-1.5 text-sm text-status-error">
              {passwordError}
            </p>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center">
            <input
              id="remember"
              type="checkbox"
              checked={data.user.remember_me}
              onChange={(e) =>
                setData('user', { ...data.user, remember_me: e.target.checked })
              }
              className="h-4 w-4 rounded border-chestnut-gray text-chestnut-forest focus:ring-chestnut-gold"
            />
            <label htmlFor="remember" className="ml-2 text-sm text-chestnut-charcoal">
              Remember me
            </label>
          </div>

          <Link href="/password/reset" className="auth-link text-sm">
            Forgot password?
          </Link>
        </div>

        <button type="submit" disabled={processing} className="auth-submit">
          {processing ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              Signing in&hellip;
            </>
          ) : (
            'Sign in'
          )}
        </button>
      </form>
    </AuthLayout>
  )
}
